import { Event, useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { Button, Icon, styles, Text, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import { L10N } from './Aura.l10n';
import * as style from './Aura.module.css';
import { Waveform } from './Aura.waveform';
import { useLiveSession } from './hooks/useLiveSession';
import { useOpenAiRealtimeSession } from './hooks/useOpenAiRealtimeSession';
import { useXaiRealtimeSession } from './hooks/useXaiRealtimeSession';
import { TYPE } from '../../../../Core.constants';
import { EVENT, ICON, METRICS_EVENTS } from '../../../helpers';
import { DEFAULT_NAME } from '../../Chat.constants';

const VOICE_MODEL = {
  REALTIME: 'realtime',
  XAI: 'xai',
};

const getVoiceSession = ({ liveSession, openAiSession, voiceModel, xaiSession }) => {
  if (voiceModel === VOICE_MODEL.REALTIME) return openAiSession;
  if (voiceModel === VOICE_MODEL.XAI) return xaiSession;
  return liveSession;
};

const Aura = ({ hotelId, onCallEnd = () => {}, onTranscript, settings }) => {
  const liveSession = useLiveSession();
  const openAiSession = useOpenAiRealtimeSession();
  const xaiSession = useXaiRealtimeSession();
  const { value: { currency, id, locale, type, urlParams = {} } = {} } = useStore();
  const voiceModel = urlParams['voice-model']?.toLowerCase();
  const { startSession, endSession, audioAnalyzers } = getVoiceSession({
    liveSession,
    openAiSession,
    voiceModel,
    xaiSession,
  });
  const { translate } = useLocale();
  const { ai: { name: assistantName = DEFAULT_NAME } = {} } = settings || {};

  const [context, setContext] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (type === TYPE.CHAIN && !hotelId) return;

    setContext({ hotelId });
  }, [hotelId, type]);

  const handleCallEnd = useCallback(() => {
    setReady(false);
    Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_CALL_END });

    Promise.resolve(endSession()).catch((error) => {
      Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_ERROR, error: error?.message });
    });

    onCallEnd();
  }, [endSession, onCallEnd]);

  useEffect(() => {
    handleCallStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context]);

  useEffect(() => {
    const callback = () => {
      if (ready) handleCallEnd();
    };

    Event.subscribe(EVENT.CHAT_CLOSE, callback);
    return () => {
      Event.unsubscribe(EVENT.CHAT_CLOSE, callback);
    };
  }, [handleCallEnd, ready]);

  const handleCallStart = async () => {
    const isChain = type === TYPE.CHAIN && !hotelId;
    const chainId = isChain ? id : undefined;

    if (!navigator.mediaDevices || (!context && !chainId)) {
      Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_ERROR });
      return;
    }

    setIsConnecting(true);
    try {
      await startSession({
        assistant_name: assistantName,
        chainId,
        currency,
        hotelId,
        isChain,
        locale,
        onTranscript,
        setIsConnecting,
        setReady,
      });
    } catch (error) {
      setIsConnecting(false);
    }
  };

  return !(!context && !(type === TYPE.CHAIN && !hotelId)) ? (
    <>
      <Waveform active={ready} conversation={audioAnalyzers} />
      <View className={style.footer}>
        {isConnecting && <Text action>{translate(L10N.LABEL_CALLING, { name: assistantName })}</Text>}
        <Button
          busy={isConnecting}
          rounded
          squared
          large
          tooltip={translate(L10N.LABEL_HANG_UP)}
          onPress={handleCallEnd}
          className={styles(style.button, ready && style.ready)}
        >
          <Icon value={ICON.HANG_UP} />
        </Button>
      </View>
    </>
  ) : null;
};

Aura.displayName = 'Mirai:Core:Chat.Aura';

Aura.propTypes = {
  hotelId: PropTypes.string,
  onCallEnd: PropTypes.func,
  onTranscript: PropTypes.func,
  settings: PropTypes.shape({
    ai: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
};

export { Aura };
